import React, { useState, useEffect, useCallback, useMemo } from "react";
import { CheckCircle, Eye } from "lucide-react";
import { authService, employeeService } from "../services/api";
import AttachmentPreviewModal from "../components/Common/AttachmentPreviewModal";
import toast from "react-hot-toast";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();

const EmployeeReports = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [payslipsByMonth, setPayslipsByMonth] = useState({});
  const [form16Data, setForm16Data] = useState([]);
  const [employeeId, setEmployeeId] = useState(null);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewState, setPreviewState] = useState({
    isOpen: false,
    url: null,
    downloadUrl: null,
    fileName: null,
    blobUrlToRevoke: null,
  });

  const fetchMyPayslips = useCallback(async (year) => {
    setLoading(true);
    try {
      const res = await employeeService.getMyPayslipsForYear(year);
      if (res?.success && Array.isArray(res.data)) {
        const map = {};
        res.data.forEach((entry) => {
          map[entry.monthYear] = entry;
        });
        setPayslipsByMonth(map);
      } else {
        setPayslipsByMonth({});
      }
    } catch (e) {
      console.error("Error fetching my payslips:", e);
      toast.error("Failed to load payslips");
      setPayslipsByMonth({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const userRes = await authService.getCurrentUser();
        const id = userRes?.data?.employeeId ?? userRes?.employeeId ?? null;
        if (!cancelled) setEmployeeId(id);
      } catch {
        if (!cancelled) setEmployeeId(null);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fetch employee profile for DOJ / service commenced (min year for payslips)
  useEffect(() => {
    if (!employeeId) {
      setEmployeeProfile(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await employeeService.getEmployee(employeeId);
        const emp = res?.data ?? res;
        if (!cancelled && emp) setEmployeeProfile(emp);
        else if (!cancelled) setEmployeeProfile(null);
      } catch {
        if (!cancelled) setEmployeeProfile(null);
      }
    })();
    return () => { cancelled = true; };
  }, [employeeId]);

  // Min year = max(company service_commences_on year, employee DOJ year). Show only from that year to current.
  const [companyServiceYear, setCompanyServiceYear] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem("companyData") : null;
    if (!raw) {
      if (!cancelled) setCompanyServiceYear(null);
      return;
    }
    try {
      const company = JSON.parse(raw);
      const serviceOn = company?.service_commences_on ?? company?.serviceCommencesOn ?? null;
      const year = serviceOn ? new Date(serviceOn).getFullYear() : null;
      if (!cancelled) setCompanyServiceYear(Number.isNaN(year) ? null : year);
    } catch {
      if (!cancelled) setCompanyServiceYear(null);
    }
    return () => { cancelled = true; };
  }, []);

  // Year options: from max(company service_commences_on, employee DOJ) up to current year
  const yearOptions = useMemo(() => {
    const doj =
      employeeProfile?.joiningDate ??
      employeeProfile?.dateOfJoin ??
      employeeProfile?.dateOfJoining ??
      employeeProfile?.serviceCommencedDate ??
      null;
    const dojYear = doj ? new Date(doj).getFullYear() : null;
    const serviceYear = companyServiceYear ?? null;
    // Min year = latest of: company service commenced, employee DOJ (so we only show from when both apply)
    const minYear = [serviceYear, dojYear].filter((y) => y != null).length
      ? Math.max(...[serviceYear, dojYear].filter((y) => y != null))
      : currentYear;
    const from = Math.min(minYear, currentYear);
    return Array.from({ length: currentYear - from + 1 }, (_, i) => currentYear - i);
  }, [employeeProfile, companyServiceYear]);

  // Clamp selected year to allowed range when yearOptions change (e.g. after DOJ loads)
  useEffect(() => {
    if (yearOptions.length && !yearOptions.includes(selectedYear)) {
      setSelectedYear(yearOptions[0]);
    }
  }, [yearOptions]);

  useEffect(() => {
    fetchMyPayslips(selectedYear);
  }, [selectedYear, fetchMyPayslips]);

  useEffect(() => {
    fetch("/api/form16")
      .then((res) => res.json())
      .then((data) => setForm16Data(Array.isArray(data) ? data : []))
      .catch(() => setForm16Data([]));
  }, []);

  const closePreview = () => {
    if (previewState.blobUrlToRevoke) {
      window.URL.revokeObjectURL(previewState.blobUrlToRevoke);
    }
    setPreviewState({
      isOpen: false,
      url: null,
      downloadUrl: null,
      fileName: null,
      blobUrlToRevoke: null,
    });
  };

  const handleViewPayslip = async (monthYear, entry) => {
    if (!employeeId) {
      toast.error("Unable to identify employee");
      return;
    }
    const type = entry?.documents?.find((d) => d.type === "standard")?.type
      ?? entry?.documents?.[0]?.type
      ?? "standard";
    try {
      const data = await employeeService.downloadPayslip(employeeId, monthYear, type);
      const blob = data instanceof Blob ? data : new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const fileName = `${monthYear}.pdf`;
      setPreviewState({
        isOpen: true,
        url,
        downloadUrl: url,
        fileName,
        blobUrlToRevoke: url,
      });
    } catch (e) {
      console.error("View payslip error:", e);
      toast.error("Failed to view payslip");
    }
  };

  const tabs = ["Payslips", "Form-16"];

  return (
    <div className="p-6">
      <div className="flex border-b border-gray-300">
        {tabs.map((tab, index) => (
          <button
            key={index}
            onClick={() => setActiveTab(index)}
            className={`py-2 px-4 text-sm font-medium ${
              activeTab === index
                ? "bg-gray-200 font-semibold border-b-2 border-blue-500"
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 0 && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold">Payslips</h3>

          <div className="mt-2">
            <label className="block text-sm font-medium text-gray-600">Select Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="mt-2 w-32 border border-gray-300 rounded-md shadow-sm p-2 text-sm"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="mt-4 text-gray-500 text-sm">Loading payslips…</div>
          ) : (
            <table className="w-full mt-4 border border-gray-300 text-sm">
              <thead>
                <tr className="bg-gray-100 text-gray-700">
                  <th className="border px-4 py-2 text-left">Month</th>
                  <th className="border px-4 py-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {MONTHS.map((month) => {
                  const monthYear = `${month}-${selectedYear}`;
                  const entry = payslipsByMonth[monthYear];
                  const hasPayslip = entry?.documents?.length > 0;
                  return (
                    <tr key={month} className="text-center">
                      <td className="border px-4 py-2">{month}</td>
                      <td className="border px-4 py-2">
                        {hasPayslip ? (
                          <button
                            type="button"
                            onClick={() => handleViewPayslip(monthYear, entry)}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-full border-2 border-blue-400 bg-white hover:bg-blue-50 text-blue-600 transition-colors"
                            title="Preview"
                          >
                            <Eye size={18} />
                          </button>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 1 && (
        <div className="mt-4">
          <h3 className="text-lg font-semibold">Form-16</h3>
          <div className="grid grid-cols-2 gap-4 mt-4">
            {["Part-A", "Part-B"].map((part, index) => (
              <div key={index}>
                <h4 className="text-sm font-medium text-gray-700">{part}</h4>
                <table className="w-full mt-2 border border-gray-300 text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-gray-700">
                      <th className="border px-4 py-2">Year</th>
                      <th className="border px-4 py-2">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form16Data.map((yearData) => (
                      <tr key={yearData.year} className="text-center">
                        <td className="border px-4 py-2">{yearData.year}</td>
                        <td className="border px-4 py-2">
                          {yearData[part] ? (
                            <CheckCircle className="text-green-500 w-5 h-5 mx-auto" />
                          ) : (
                            <span className="text-gray-400">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}

      <AttachmentPreviewModal
        isOpen={previewState.isOpen}
        url={previewState.url}
        downloadUrl={previewState.downloadUrl}
        fileName={previewState.fileName}
        onClose={closePreview}
      />
    </div>
  );
};

export default EmployeeReports;
